import { useEffect } from 'react';

import type { DescriptionPage_PremiumExperienceVariant as PremiumExperienceVariant } from '__generated__/graphql-types';
import { compose } from 'recompose';

import type { Partners } from 'bundles/coursera-plus/utils/subscriptionTiersUtils';
import {
  generateSubscriptionTiersImpression,
  getIsStandaloneCourseEligibleForTiersEnroll,
  initSubscriptionTiersFullStory,
} from 'bundles/coursera-plus/utils/subscriptionTiersUtils';
import withEnrollment from 'bundles/enroll/components/xdp/withEnrollment';
import type { PropsFromWithEnrollment } from 'bundles/enroll/components/xdp/withEnrollment';
import type { PropsFromWithPromotionInfo } from 'bundles/promotions/components/withPromotionInfo';
import withPromotionInfo from 'bundles/promotions/components/withPromotionInfo';

export type PropsFromCaller = {
  isSubscribedToCPlusOrLite?: boolean;
  hasSpecialization: boolean;
  partners?: Partners;
  premiumExperienceVariant?: PremiumExperienceVariant | null;
  // Note: at least one of `courseId` or `s12nId` is needed for the `withEnrollment` HOC
  // eslint-disable-next-line react/no-unused-prop-types
  courseId?: string;
  // eslint-disable-next-line react/no-unused-prop-types
  s12nId?: string;
};

type PropsToComponent = PropsFromCaller & PropsFromWithEnrollment & PropsFromWithPromotionInfo;

const SubscriptionTiersImpression = ({
  isSubscribedToCPlusOrLite,
  hasSpecialization,
  partners,
  premiumExperienceVariant,
  enrollmentAvailableChoices,
  promotionEligibilities,
}: PropsToComponent) => {
  const isStandaloneCourseEligibleForTiersEnroll = getIsStandaloneCourseEligibleForTiersEnroll({
    hasSpecialization,
    premiumExperienceVariant,
    enrollmentAvailableChoices,
  });
  const hasPromotion = Boolean(promotionEligibilities?.isEligible ? promotionEligibilities?.promoCodeId : undefined);

  useEffect(() => {
    generateSubscriptionTiersImpression({
      hasSpecialization,
      partners,
      isStandaloneCourseEligibleForTiersEnroll,
      hasPromotion,
    });

    if (!isSubscribedToCPlusOrLite) {
      initSubscriptionTiersFullStory();
    }
  }, [isSubscribedToCPlusOrLite, hasSpecialization, partners, isStandaloneCourseEligibleForTiersEnroll, hasPromotion]);

  return null;
};

export default compose<PropsToComponent, PropsFromCaller>(
  withPromotionInfo(),
  withEnrollment()
)(SubscriptionTiersImpression);
